import { useState, useEffect } from 'react';
import { getClientIdWithTimeout } from './analytics';
import { platformsConsts, resolvePlatform } from './platformResolver';

type DownloadLinks = Record<keyof typeof platformsConsts, string>;

const defaultLinks: DownloadLinks = {
  ios: 'https://clearvpn.onelink.me/uPP4/2f476c20',
  macos: 'https://dl.devmate.com/com.macpaw.clearvpn.macos-site-ver/ClearVPN.dmg',
  windows: 'https://s3.amazonaws.com/clearvpn.com/downloads/windows/SetupClearVPN.exe',
  android: 'https://clearvpn.onelink.me/uPP4/bb442fd0',
};

const fetchWindowsDownloadLink = (clientId: string): Promise<string> => {
  return fetch('https://api.clearvpn.com/web/v1/marketing/download-app-installer', {
    method: 'POST',
    body: JSON.stringify({
      app_identity: 'com.macpaw.clearvpn.windows',
      analytics_data: `cid=${clientId}`,
    }),
  })
    .then((res) => res.json())
    .then(({ download_url: downloadURL }) => atob(downloadURL as string));
};

/**
 * Returns a promise that resolves into an object with download links with analytics id.
 */
const fetchPersonalizedLinks = (): Promise<DownloadLinks> => {
  let clientId: string;

  return getClientIdWithTimeout()
    .then((cid) => {
      if (!cid) {
        throw new Error('Failed to get cid');
      }

      clientId = cid;

      return fetchWindowsDownloadLink(clientId)
        .catch((err: unknown) => {
          // in case fetching Windows download link fails, we can still append `cid` to the rest of the links
          console.error(err);

          return defaultLinks.windows;
        })
        .then((windowsDownloadURL) => ({
          ios: `${defaultLinks.ios}?cid=${clientId}`,
          macos: `${defaultLinks.macos}?cid=${clientId}`,
          windows: windowsDownloadURL,
          android: `${defaultLinks.android}?cid=${clientId}`,
        }));
    })
    .catch((err: unknown) => {
      console.error('Couldn’t get cid', err);

      return defaultLinks;
    });
};

const useDownloadLinks = () => {
  const [links, setLinks] = useState<DownloadLinks>(defaultLinks);
  const [platform, setPlatform] = useState<keyof typeof platformsConsts>();
  const [hasFetchedLinks, setHasFetchedLinks] = useState<boolean>(false);

  useEffect(() => {
    setPlatform(resolvePlatform());
    void fetchPersonalizedLinks()
      .then((personalizedLinks) => {
        setLinks(personalizedLinks);
      })
      .finally(() => {
        setHasFetchedLinks(true);
      });
  }, []);

  return {
    links,
    currentPlatformLink: links[platform ?? platformsConsts.macos],
    hasFetchedPersonalizedLinks: hasFetchedLinks,
  };
};

export { useDownloadLinks };
